import React from 'react';
import { CKEditorConfig } from '../CKEditor/config/CKEditorConfig';
import { getCKEditorConfig } from '../CKEditor/config/EditorConfig';
import { QualioSectionedContent } from '../CKEditor/models';
import { QualioAttachmentCKUploadAdapter } from '../CKEditor/plugins/Attachment/QualioAttachmentCKUploadAdapter';
import { updateEditorSelectionToEditableRegion } from '../CKEditor/plugins/LockSectionHeaders/utils';
import { PresentationUtils } from '../CKEditor/util/PresentationUtils';
import { ToolbarManager, useToolbarManager } from '../CKEditor/util/ToolbarManager';
import styles from './ContentEditor.module.css';
import { CONTENT_EDITOR_TOOLBAR_ITEMS } from '../CKEditor/config/ToolbarItems';
import { setUpScrollHandler } from '../CKEditor/util/scrollHandler';
import {
  ToolbarManagerWithScrollHandler,
  useToolbarManagerWithScrollHandler,
} from '../CKEditor/util/ToolbarManagerWithScrollHandler';
import { Editor, DecoupledEditor } from 'ckeditor5';
import { CKEditor } from '@ckeditor/ckeditor5-react';

const configureUploadAdapter = (editor: Editor) => {
  const fileRepositoryPlugin = editor.plugins.get('FileRepository');
  fileRepositoryPlugin.createUploadAdapter = (loader: any) => {
    return new QualioAttachmentCKUploadAdapter(loader);
  };
};

export const setupClickListener = (editor: Editor): void => {
  editor.editing.view.listenTo(editor.editing.view.document, 'click', (evt: any, data: any) => {
    const clickedViewElement = data.target;
    const href = clickedViewElement.getAttribute('href');
    // always open smart links in new tab
    if (clickedViewElement.getAttribute('data-mention') && href) {
      window.open(href, '_blank');
      return;
    }
  });
};

function mathCharactersExtended(editor: Editor): void {
  const scp = editor.plugins.get('SpecialCharacters');
  scp.addItems('Mathematical', [{ title: 'delta', character: 'Δ' }]);
}

const createEditorReadyHandler =
  (
    toolbarManager: ToolbarManager | ToolbarManagerWithScrollHandler,
    isSectionedContent: boolean,
    focusOnRender: boolean,
    allowScrollHandler = false,
  ) =>
  (editor: Editor): void => {
    if (focusOnRender) {
      editor.focus();
    }
    if (allowScrollHandler) {
      const scrollHandler = () => {
        editor.focus();
      };
      setUpScrollHandler(scrollHandler);
    }
    if (isSectionedContent) {
      updateEditorSelectionToEditableRegion(editor);
    }
    setupClickListener(editor);
    mathCharactersExtended(editor);
    toolbarManager.setToolbarFromEditor(editor);
    configureUploadAdapter(editor);
    editor.model.schema.setAttributeProperties('htmlAttributes', {
      isFormatting: true,
    });
  };

export const ContentEditor: React.FC<{
  sectionedData?: QualioSectionedContent;
  data?: string;
  companyId: number;
  handleAutoSave?: (editor: Editor) => Promise<void>;
  onChange?: (event: any, editor: Editor) => void;
  toolbarItems?: string[];
  toolbarParentId?: string;
  allowSectionsAnchored?: boolean;
  onEditorReady?: (editor: Editor) => void;
}> = ({
  sectionedData,
  data,
  companyId,
  handleAutoSave,
  onChange,
  toolbarItems = CONTENT_EDITOR_TOOLBAR_ITEMS,
  toolbarParentId = 'pageHeaderSection',
  allowSectionsAnchored = false,
  onEditorReady,
}) => {
  const editorConfig: CKEditorConfig = {
    ...getCKEditorConfig(companyId, toolbarItems, handleAutoSave),
  };

  const toolbarManager = useToolbarManager(toolbarParentId);
  const toolbarManagerWithScrollHandler = useToolbarManagerWithScrollHandler();
  const toolbarCSS = toolbarParentId === 'pageHeaderSection' ? '' : styles['form-toolbar'];
  const contentCSS = toolbarParentId === 'pageHeaderSection' ? '' : styles['form-content'];

  // toolbarParentid is only present for Form Rich Text Editor
  // Hence comparing with default value to validate it
  const focusOnRender = allowSectionsAnchored ? false : toolbarParentId === 'pageHeaderSection';

  return (
    <>
      <div className={`${styles['sections-editor__heading-section']} ${toolbarCSS}`} id={toolbarParentId} />
      <div
        data-cy={'ContentEditor'}
        id="ContentEditor"
        className={`${styles['sections-editor__document__section-content']} ${contentCSS} document `}
      >
        <CKEditor
          onReady={(editor) => {
            createEditorReadyHandler(
              allowSectionsAnchored ? toolbarManagerWithScrollHandler : toolbarManager,
              sectionedData !== undefined,
              focusOnRender,
              allowSectionsAnchored,
            )(editor);
            onEditorReady?.(editor);
          }}
          onChange={onChange}
          editor={DecoupledEditor}
          watchdogConfig={{ saveInterval: 30000 }}
          data={sectionedData ? PresentationUtils.determineEditorContentFromSectionsData(sectionedData) : data}
          config={editorConfig}
        />
      </div>
    </>
  );
};
